import React, { useState } from 'react';
import {
  AppBar, Box, Toolbar, MenuItem, useTheme, useMediaQuery,
  IconButton, Drawer, List, ListItemText,
  Divider, Typography, ListItemButton,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { getAppBarStyles, toolbarStyles } from './styles/NavBarStyles';
import { NavBarLogo } from './components/NavBarLogo';
import { NavBarTitle } from './components/NavBarTitle';

import { LogoutButton } from './components/LogoutButton';
import ThemeToggle from './components/ThemeToggle';
import { useNavBarCommon } from './hooks/useNavBarCommon';
import SoporteModal from '../modals/soporte';
import NavMenu from './dealership/NavMenu';
import { useAppTheme } from '../../context/theme.context';
import {
  GROUP_NAV_SECTIONS,
  getVisibleNavSections,
  isRouteAllowed,
} from '../../utils/routing/routePermissions';

const NavBar = () => {
  const {
    currentUser,
    handleLogout,
    handleNavigate,
    isActive,
    beginUnitSelection,
    canChangeWorkspace,
  } = useNavBarCommon();
  const { colors } = useAppTheme();
  const [isSoporteModalOpen, setIsSoporteModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  const visibleSections = getVisibleNavSections(
    GROUP_NAV_SECTIONS,
    currentUser,
  );

  const handleCloseSoporteModal = () => setIsSoporteModalOpen(false);

  const handleMenuEnter = (menuId, event) => {
    setAnchorEl({ [menuId]: event.currentTarget });
  };

  const handleMenuToggle = (menuId, event) => {
    if (anchorEl[menuId]) {
      handleMenuClose(menuId);
    } else {
      handleMenuEnter(menuId, event);
    }
  };

  const handleMenuClose = (menuId) => {
    // Evita el warning de foco retenido dentro de un Menu aria-hidden.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setAnchorEl((prev) => {
      const newAnchorEl = { ...prev };
      delete newAnchorEl[menuId];
      return newAnchorEl;
    });
  };

  const handleMenuNavigate = (path, menuId) => {
    if (menuId) handleMenuClose(menuId);
    handleNavigate(path);
  };

  const handleMobileNav = (path) => {
    setMobileMenuOpen(false);
    handleNavigate(path);
  };

  return (
    <Box sx={{ flexGrow: 1, mb: 3 }}>
      <AppBar position="static" sx={getAppBarStyles(colors)}>
        <Toolbar sx={toolbarStyles}>
          <NavBarLogo onHomeClick={() => handleNavigate('/')} />

          <NavBarTitle title={currentUser?.groupName} />

          {canChangeWorkspace && !isMobile && (
            <Button color="inherit" size="small" onClick={beginUnitSelection}>
              Cambiar grupo
            </Button>
          )}

          {!isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {visibleSections.map((section) => {
                const menuId = `${section.id}-menu`;
                return (
                  <NavMenu
                    key={section.id}
                    menuId={menuId}
                    label={section.label}
                    anchorEl={anchorEl[menuId]}
                    onMenuToggle={handleMenuToggle}
                    onMenuLeave={handleMenuClose}
                  >
                    {section.items.map((item) => (
                      <MenuItem
                        key={item.path}
                        onClick={() => handleMenuNavigate(item.path, menuId)}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </NavMenu>
                );
              })}
            </Box>
          ) : (
            <IconButton
              color="inherit"
              onClick={() => setMobileMenuOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <ThemeToggle />
          <LogoutButton onLogout={handleLogout} />
        </Toolbar>
      </AppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            backgroundColor: colors.surface || '#fff',
            backgroundImage: 'none',
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 2,
            background: colors.navBar?.gradient || colors.primary,
            color: '#fff',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {currentUser?.groupName || 'Menú'}
          </Typography>
          <IconButton size="small" onClick={() => setMobileMenuOpen(false)} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ pt: 1 }}>
          {/* Home */}
          {isRouteAllowed('/', currentUser) && (
            <ListItemButton
              onClick={() => handleMobileNav('/')}
              selected={isActive('/')}
              sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
            >
              <ListItemText
                primary="Inicio"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          )}

          {visibleSections.map((section, sectionIndex) => (
            <Box key={section.id}>
              {sectionIndex > 0 && <Divider sx={{ my: 1.5, mx: 2 }} />}
              <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textSecondary || 'text.secondary',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: 1.2,
                  }}
                >
                  {section.label}
                </Typography>
              </Box>
              {section.items.map((item) => (
                <ListItemButton
                  key={item.path}
                  onClick={() => handleMobileNav(item.path)}
                  sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </Box>
          ))}

          {canChangeWorkspace && (
            <>
              <Divider sx={{ my: 1.5, mx: 2 }} />
              <ListItemButton
                onClick={() => {
                  setMobileMenuOpen(false);
                  beginUnitSelection();
                }}
                sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
              >
                <ListItemText primary="Cambiar grupo" />
              </ListItemButton>
            </>
          )}
        </List>
      </Drawer>

      <SoporteModal
        open={isSoporteModalOpen}
        handleClose={handleCloseSoporteModal}
      />
    </Box>
  );
};

export default NavBar;
